import { useEffect, useMemo, useState } from 'react';
import { Save, Settings2 } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { AutocompleteSelect, Button } from '../../ui';
import { OverrideMode, ProviderType } from '../../gql/types';

// Inline editor for a key's routing override (US-8.1.2 · setApiKeyOverride).
//
// Mode is validated against the contract's OverrideMode enum; pinnedProvider is required iff
// mode is PIN (and ignored otherwise). The Save button is disabled until the selection is both
// valid and actually changed, so we never fire a no-op mutation.

const MODE_OPTIONS = [
  { value: OverrideMode.Auto, label: 'Auto — follow routing policy' },
  { value: OverrideMode.ForceInternal, label: 'Force internal' },
  { value: OverrideMode.ForceExternal, label: 'Force external' },
  { value: OverrideMode.Pin, label: 'Pin to provider' },
] as const;

const PROVIDER_OPTIONS = [
  { value: ProviderType.InHouse, label: 'In-house' },
  { value: ProviderType.Openai, label: 'OpenAI' },
  { value: ProviderType.Anthropic, label: 'Anthropic' },
  { value: ProviderType.Gemini, label: 'Gemini' },
  { value: ProviderType.Openrouter, label: 'OpenRouter' },
] as const;

const MODE_VALUES = new Set<string>(MODE_OPTIONS.map((o) => o.value));
const PROVIDER_VALUES = new Set<string>(PROVIDER_OPTIONS.map((o) => o.value));

export interface OverrideSelection {
  overrideMode: OverrideMode;
  pinnedProvider: ProviderType | null;
}

export interface OverrideEditorProps {
  mode: OverrideMode;
  pinnedProvider: ProviderType | null;
  loading?: boolean | undefined;
  onSave: (selection: OverrideSelection) => void;
}

export function OverrideEditor({
  mode,
  pinnedProvider,
  loading = false,
  onSave,
}: OverrideEditorProps) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const [draftMode, setDraftMode] = useState<OverrideMode>(mode);
  const [draftProvider, setDraftProvider] = useState<ProviderType | null>(pinnedProvider);

  // Re-sync when the underlying key changes (e.g. after a successful save elsewhere).
  useEffect(() => {
    setDraftMode(mode);
    setDraftProvider(pinnedProvider);
  }, [mode, pinnedProvider]);

  const requiresProvider = draftMode === OverrideMode.Pin;

  const validation = useMemo(() => {
    if (!MODE_VALUES.has(draftMode)) return { valid: false, provider: null };
    if (requiresProvider) {
      if (!draftProvider || !PROVIDER_VALUES.has(draftProvider)) {
        return { valid: false, provider: null };
      }
      return { valid: true, provider: draftProvider };
    }
    // Non-pin modes carry no pinned provider.
    return { valid: true, provider: null };
  }, [draftMode, draftProvider, requiresProvider]);

  const changed =
    draftMode !== mode || (validation.provider ?? null) !== (pinnedProvider ?? null);

  const handleSave = () => {
    if (!validation.valid || !changed) return;
    onSave({ overrideMode: draftMode, pinnedProvider: validation.provider });
  };

  return (
    <div
      className={`rounded-xl border p-4 ${
        isDark ? 'border-white/[0.06] bg-white/[0.02]' : 'border-slate-200 bg-slate-50'
      }`}
    >
      <div className="mb-3 flex items-center gap-2">
        <Settings2
          className={`h-4 w-4 ${isDark ? 'text-white/50' : 'text-slate-500'}`}
          aria-hidden="true"
        />
        <h4
          className={`text-xs font-medium uppercase tracking-widest ${
            isDark ? 'text-white/40' : 'text-slate-500'
          }`}
        >
          Routing override
        </h4>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <span
            className={`mb-1.5 block text-xs font-medium ${
              isDark ? 'text-white/50' : 'text-slate-500'
            }`}
            id="override-mode-label"
          >
            Mode
          </span>
          <AutocompleteSelect
            label="Override mode"
            value={draftMode}
            options={MODE_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
            onChange={(next) => setDraftMode(next as OverrideMode)}
          />
        </div>

        <div>
          <span
            className={`mb-1.5 block text-xs font-medium ${
              isDark ? 'text-white/50' : 'text-slate-500'
            }`}
          >
            Pinned provider{requiresProvider ? ' (required)' : ''}
          </span>
          <AutocompleteSelect
            label="Pinned provider"
            value={requiresProvider ? draftProvider : null}
            disabled={!requiresProvider}
            placeholder={requiresProvider ? 'Choose a provider…' : 'Not applicable'}
            options={PROVIDER_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
            onChange={(next) => setDraftProvider(next as ProviderType)}
          />
        </div>
      </div>

      <div className="mt-4 flex justify-end">
        <Button
          variant="primary"
          size="sm"
          loading={loading}
          disabled={!validation.valid || !changed}
          onClick={handleSave}
          leftIcon={<Save className="h-4 w-4" aria-hidden="true" />}
        >
          Save override
        </Button>
      </div>
    </div>
  );
}
